import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, X, Plus, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../lib/api";
import { formatPrice } from "../../../lib/formatPrice";
import LabBookingLayout from "../components/LabBookingLayout";
import { useLabBooking } from "../context/LabBookingContext";

// Same composite score used on the Flutter Compare Labs page:
// score = total price + (distance_km * ₹10 travel cost). Lowest score wins,
// only fully-matched labs are eligible.
const TRAVEL_COST_PER_KM = 10;

const CompareLabsPage = () => {
  const navigate = useNavigate();
  const {
    selectedTests,
    removeTest,
    setSelectedLab,
    location,
    locationStatus,
    requestLocation,
    collectionType,
    setCollectionType,
    user,
    pendingLabBooking,
    setPendingLabBooking,
  } = useLabBooking();

  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { requestLocation(); }, [requestLocation]);

  const testMasterIds = selectedTests.filter((t) => !t.is_package && !t.is_unmapped).map((t) => t.test_master_id);
  const testNames     = selectedTests.filter((t) => t.is_unmapped).map((t) => t.name);
  const packageNames  = selectedTests.filter((t) => t.is_package).map((t) => t.name);

  const collectionTypeNum = collectionType === "lab" ? 1 : 2;

  useEffect(() => {
    if (!location || (testMasterIds.length === 0 && testNames.length === 0 && packageNames.length === 0)) {
      setLabs([]);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .post("/user/lab/compare", {
        test_master_ids: testMasterIds.join(","),
        test_names:      testNames.join(","),
        package_names:   packageNames.join(","),
        collection_type: collectionTypeNum,
      })
      .then((res) => setLabs(res.data?.data?.labs || []))
      .catch(() => setError("Couldn't load labs, try again."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, collectionTypeNum, testMasterIds.join(","), testNames.join(","), packageNames.join(",")]);

  const recommendedLabId = useMemo(() => {
    let bestId = null, bestScore = null;
    for (const lab of labs) {
      if (lab.matched_count !== lab.total_count) continue;
      const charge = Number(lab.home_collection_charge || 0);
      const score  = Number(lab.total_amount) + charge + (lab.lab_distance || 0) / 1000 * TRAVEL_COST_PER_KM;
      if (bestScore === null || score < bestScore) { bestScore = score; bestId = lab.lab_id; }
    }
    return bestId;
  }, [labs]);

  // Per-lab booking state — avoids showing one lab's error on all cards.
  const [bookingLabId, setBookingLabId] = useState(null);
  const [bookErrors, setBookErrors]     = useState({}); // { [lab_id]: message }

  const handleBook = (lab) => {
    if (!user) {
      // Store what the user tried to book, complete cart call after login.
      setPendingLabBooking({
        lab,
        testIds: (lab.tests || []).map((t) => t.lab_test_id).join(","),
        collectionType,
      });
      navigate("/lab-booking/login");
      return;
    }

    const testIds = (lab.tests || []).map((t) => t.lab_test_id).join(",");
    setBookingLabId(lab.lab_id);
    setBookErrors((prev) => ({ ...prev, [lab.lab_id]: null }));

    api
      .post("/user/lab/cart", {
        lab_id: lab.lab_id,
        test_ids: testIds,
        test_collection_type: collectionTypeNum,
      })
      .then((res) => {
        if (res.data?.code === 200) {
          setSelectedLab({ lab: res.data.data.lab, testList: res.data.data.test_list });
          navigate("/lab-booking/cart");
        } else {
          setBookErrors((prev) => ({
            ...prev,
            [lab.lab_id]: res.data?.message || "Couldn't add to cart, try again.",
          }));
        }
      })
      .catch(() =>
        setBookErrors((prev) => ({
          ...prev,
          [lab.lab_id]: "Couldn't add to cart, try again.",
        }))
      )
      .finally(() => setBookingLabId(null));
  };

  // If we just came back from login with a pendingLabBooking already cleared
  // (handled inside LoginPage), nothing special to do here.

  return (
    <LabBookingLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-[13px] font-semibold text-[#3D4945] hover:text-[#13B58E] mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <h1 className="text-[22px] font-extrabold text-[#191C1D] mb-4">Compare Labs</h1>

      {/* Collection type toggle — same as mobile app, shown before lab results */}
      <div className="flex bg-[#F3F6F7] rounded-full p-1 mb-5 w-fit">
        <button
          onClick={() => setCollectionType("home")}
          className={`text-[13px] font-semibold px-5 py-2 rounded-full transition ${
            collectionType === "home" ? "bg-[#13B58E] text-white" : "text-[#6B7A99]"
          }`}
        >
          Home Collection
        </button>
        <button
          onClick={() => setCollectionType("lab")}
          className={`text-[13px] font-semibold px-5 py-2 rounded-full transition ${
            collectionType === "lab" ? "bg-[#13B58E] text-white" : "text-[#6B7A99]"
          }`}
        >
          Lab Visit
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {selectedTests.length === 0 && (
          <span className="text-[12px] text-[#6B7A99]">No tests selected — add some from the home page.</span>
        )}
        {selectedTests.map((test) => (
          <span
            key={test.is_package ? `package:${test.name}` : test.is_unmapped ? `unmapped:${test.name}` : `test:${test.test_master_id}`}
            className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-gray-200 bg-white text-[#3D4945]"
          >
            {test.name}
            <button onClick={() => removeTest(test)}><X size={12} /></button>
          </span>
        ))}
        <button
          onClick={() => navigate("/?section=lab-booking")}
          className="flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-full border border-dashed border-[#13B58E] text-[#13B58E]"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {!location && (locationStatus === "idle" || locationStatus === "loading") && (
        <div className="flex items-center gap-2 text-[13px] text-[#6B7A99] py-6">
          <Loader2 size={16} className="animate-spin" /> Getting your location to find nearby labs...
        </div>
      )}
      {!location && locationStatus === "denied" && (
        <p className="text-[13px] text-[#B45309] py-6">
          Location access is needed to compare nearby labs.{" "}
          <button onClick={requestLocation} className="underline font-medium">Try again</button>
        </p>
      )}
      {!location && locationStatus === "unsupported" && (
        <p className="text-[13px] text-[#B45309] py-6">Your browser doesn't support location access.</p>
      )}
      {!!location && loading && (
        <div className="flex items-center gap-2 text-[13px] text-[#6B7A99] py-6">
          <Loader2 size={16} className="animate-spin" /> Finding nearby labs...
        </div>
      )}
      {!!location && !loading && error && (
        <p className="text-[13px] text-red-500 py-6">{error}</p>
      )}
      {!!location && !loading && !error && selectedTests.length > 0 && labs.length === 0 && (
        <p className="text-[13px] text-[#6B7A99] py-6">No labs near you offer these tests yet.</p>
      )}
      {!user && labs.length > 0 && (
        <p className="text-[12px] text-[#B45309] mb-3">You'll be asked to sign in when you click Book.</p>
      )}

      <div className="space-y-4 max-w-2xl">
        {!loading && !error && labs.map((lab) => {
          const isRecommended = lab.lab_id === recommendedLabId;
          const mrpTotal      = (lab.tests || []).reduce((sum, t) => sum + Number(t.price || 0), 0);
          const isBooking     = bookingLabId === lab.lab_id;
          const labError      = bookErrors[lab.lab_id];

          return (
            <div
              key={lab.lab_id}
              className={`bg-white rounded-2xl p-5 ${isRecommended ? "border-2 border-[#13B58E]" : "border border-gray-100"}`}
            >
              {isRecommended && (
                <span className="inline-block text-[11px] font-semibold text-[#13B58E] bg-[#EEF3F2] px-2.5 py-1 rounded-full mb-2">
                  ⭐ Recommended
                </span>
              )}
              <p className="text-[15px] font-bold text-[#191C1D]">{lab.lab_name}</p>
              <p className="text-[12px] text-[#6B7A99] mt-0.5">
                {lab.matched_count}/{lab.total_count} tests matched · {lab.lab_area}
                {lab.lab_distance != null ? ` · ${Math.round(lab.lab_distance / 1000)} km` : ""}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(lab.tests || []).slice(0, 4).map((t) => (
                  <span key={t.lab_test_id} className="text-[10px] px-2 py-0.5 rounded-full bg-[#EEF3F2] text-[#13B58E] font-medium">
                    {t.name}
                  </span>
                ))}
              </div>
              <div className="mt-4 border-t border-gray-100 pt-3">
                <div className="flex justify-between text-[12px] text-[#6B7A99] mb-1">
                  <span>Tests Subtotal</span>
                  <span>₹{formatPrice(lab.total_amount)}</span>
                </div>
                {lab.home_collection_available && collectionType === "home" && (
                  <div className="flex justify-between text-[12px] text-[#6B7A99] mb-1">
                    <span>Home Collection</span>
                    <span className={lab.home_collection_charge === 0 ? "text-[#13B58E] font-semibold" : ""}>
                      {lab.home_collection_charge === null || lab.home_collection_charge === undefined
                        ? "—"
                        : Number(lab.home_collection_charge) > 0
                        ? `+₹${formatPrice(lab.home_collection_charge)}`
                        : "Free"}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[18px] font-extrabold text-[#191C1D]">
                      ₹{formatPrice(Number(lab.total_amount) + Number(lab.home_collection_charge || 0))}
                    </span>
                    {mrpTotal > Number(lab.total_amount) && (
                      <span className="text-[12px] text-[#9AA6A4] line-through">₹{formatPrice(mrpTotal)}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleBook(lab)}
                    disabled={!!bookingLabId}
                    className="bg-[#13B58E] text-white text-[13px] font-semibold px-5 py-2 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
                  >
                    {isBooking ? <Loader2 size={14} className="animate-spin inline" /> : "Book"}
                  </button>
                </div>
              </div>
              {labError && <p className="text-[12px] text-red-500 mt-2">{labError}</p>}
            </div>
          );
        })}
      </div>
    </LabBookingLayout>
  );
};

export default CompareLabsPage;
